import { Component, Inject, ChangeDetectionStrategy } from '@angular/core';
import { CommonModule } from '@angular/common';
import {
	MAT_DIALOG_DATA,
	MatDialogModule,
	MatDialogRef,
} from '@angular/material/dialog';
import { MatButtonModule } from '@angular/material/button';
import { MatDividerModule } from '@angular/material/divider';
import { MatIconModule } from '@angular/material/icon';
import { PromiseToPay } from '@lmc-app/core/services/promise-to-pay.service';
import {
	ARRANGEMENT_TYPE_LABELS,
	PAYMENT_METHOD_LABELS,
	PromiseToPayStatus,
	PromiseToPayType,
} from '@lmc-app/shared/enum/promise-to-pay-status.enum';
import { FormatUtil } from '@lmc-app/shared/utils/format.util';
import { PromiseToPayTimelineComponent } from '@lmc-app/loan-servicing/promise-to-pay/timeline/promise-to-pay-timeline.component';

export interface PromiseToPayDetailDialogData {
	promise: PromiseToPay;
}

/**
 * A promise's facts and its full history. Read-only — every action lives on the row it was
 * opened from, so this cannot become a second, divergent edit surface.
 */
@Component({
	selector: 'app-promise-to-pay-detail-dialog',
	standalone: true,
	imports: [
		CommonModule,
		MatDialogModule,
		MatButtonModule,
		MatDividerModule,
		MatIconModule,
		PromiseToPayTimelineComponent,
	],
	templateUrl: './promise-to-pay-detail-dialog.component.html',
	changeDetection: ChangeDetectionStrategy.Eager,
	styleUrls: ['./promise-to-pay-detail-dialog.component.scss'],
})
export class PromiseToPayDetailDialogComponent {
	readonly FormatUtil = FormatUtil;
	readonly PromiseToPayStatus = PromiseToPayStatus;
	readonly promise: PromiseToPay;

	constructor(
		private readonly dialogRef: MatDialogRef<PromiseToPayDetailDialogComponent>,
		@Inject(MAT_DIALOG_DATA) data: PromiseToPayDetailDialogData
	) {
		this.promise = data.promise;
	}

	get typeLabel(): string {
		if (this.promise.type !== PromiseToPayType.Arrangement) {
			return 'Promise to Pay';
		}
		return this.promise.arrangementType
			? ARRANGEMENT_TYPE_LABELS[this.promise.arrangementType]
			: 'Arrangement';
	}

	get paymentMethodLabel(): string {
		return (
			PAYMENT_METHOD_LABELS[this.promise.paymentMethod] ??
			this.promise.paymentMethod
		);
	}

	onClose(): void {
		this.dialogRef.close();
	}
}
